import { useState } from 'react';

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export interface SiteDraft<T> {
  draft: T;
  setDraft: (next: T) => void;
  /** True while the draft differs from what the server last said. */
  dirty: boolean;
  saving: boolean;
  /** Runs one save for this site only; other sites' drafts are never touched. */
  save: (write: (draft: T) => Promise<unknown>) => Promise<void>;
  reset: () => void;
}

/**
 * A local draft for one site's (or one form's) values. When the server sends new values (any save anywhere
 * reloads the page data), a clean draft follows them, but a draft with unsaved edits keeps those edits.
 * Each editor owns its draft, so saving one site can never reset, disable or overwrite another.
 */
export function useSiteDraft<T>(server: T): SiteDraft<T> {
  const [draft, setDraftState] = useState<T>(server);
  const [saving, setSaving] = useState(false);
  // The server values the draft was last compared with. Kept in state (not a ref mutated during render) so
  // StrictMode's double render cannot lose the adoption below.
  const [baseline, setBaseline] = useState<T>(server);

  let current = draft;
  if (!same(baseline, server)) {
    const wasClean = same(draft, baseline);
    setBaseline(server);
    // Adopting during render keeps the next paint consistent; React re-renders with the new state.
    if (wasClean && !same(draft, server)) {
      setDraftState(server);
      current = server;
    }
  }

  return {
    draft: current,
    setDraft: setDraftState,
    dirty: !same(current, server),
    saving,
    save: async (write) => {
      setSaving(true);
      try {
        await write(current);
      } finally {
        setSaving(false);
      }
    },
    reset: () => setDraftState(server),
  };
}
